type Point = { x: number; y: number; z?: number }; // z is optional, assumed 0

// Ramer-Douglas-Peucker algorithm to simplify a line segment
function simplifyRDP(points: Point[], epsilon: number): Point[] {
  if (points.length <= 2) return points;

  const result: Point[] = [];
  let maxDistance = 0;
  let index = 0;

  // Find point with maximum perpendicular distance
  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], points[0], points[points.length - 1]);
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  // If max distance is greater than epsilon, recursively simplify
  if (maxDistance > epsilon) {
    const left = simplifyRDP(points.slice(0, index + 1), epsilon);
    const right = simplifyRDP(points.slice(index), epsilon);
    result.push(...left.slice(0, -1), ...right);
  } else {
    result.push(points[0], points[points.length - 1]);
  }

  return result;
}

// Calculate perpendicular distance from point to line segment
function perpendicularDistance(point: Point, lineStart: Point, lineEnd: Point): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const mag = Math.sqrt(dx * dx + dy * dy);
  if (mag === 0) return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);

  const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (mag * mag)));
  const projectionX = lineStart.x + t * dx;
  const projectionY = lineStart.y + t * dy;
  return Math.sqrt((point.x - projectionX) ** 2 + (point.y - projectionY) ** 2);
}

// Interpolate points along a segment
function interpolateSegment(start: Point, end: Point, maxPoints: number): Point[] {
  const result: Point[] = [start];
  for (let i = 1; i < maxPoints - 1; i++) {
    const t = i / (maxPoints - 1);
    result.push({
      x: start.x + t * (end.x - start.x),
      y: start.y + t * (end.y - start.y),
      z: 0
    });
  }
  result.push(end);
  return result;
}

// Ensure max 5 points per segment
function enforceMaxPointsPerSegment(points: Point[], maxPoints: number): Point[] {
  const result: Point[] = [];
  const n = points.length;

  for (let i = 0; i < n - 1; i++) {
    const start = points[i];
    const end = points[(i + 1) % n];
    result.push(start);

    const segment = [start, end];
    const simplified = simplifyRDP(segment, 0); // Use 0 epsilon for straight segment
    if (simplified.length > maxPoints) {
      const interpolated = interpolateSegment(start, end, maxPoints);
      result.push(...interpolated.slice(1));
    } else {
      if (i < n - 2) result.push(end); // Avoid adding end for last segment
    }
  }

  return result;
}

// Check if two points are very close
function arePointsClose(p1: Point, p2: Point, threshold: number = 0.0001): boolean {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2) < threshold;
}

export function smoothPolygon(points: Point[], epsilon: number, maxPointsPerSegment: number = 5): Point[] {
  // Validate input
  if (points.length < 3) return points;

  // Ensure z=0 and handle near-identical closing point
  let flatPoints = points.map(p => ({ x: p.x, y: p.y, z: 0 }));
  if (arePointsClose(flatPoints[0], flatPoints[flatPoints.length - 1])) {
    flatPoints = flatPoints.slice(0, -1); // Remove near-identical last point
  }

  // Step 1: Simplify entire polygon using RDP as an open path
  let simplified = simplifyRDP(flatPoints, epsilon);

  // Step 2: Ensure max points per segment
  simplified = enforceMaxPointsPerSegment(simplified, maxPointsPerSegment);

  // Step 3: Close the polygon by adding the first point as the last point
  simplified.push({ ...simplified[0] });

  return simplified;
}
